import React from 'react';
import { Grid } from '@mui/material';
import { Activity } from '@restore/services-lib';
import { FormFieldInterface } from 'components/Form/Form.types';
import Form from 'components/Form/Form';
import TagMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import UserSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/UsersMultiSelectDropdown';
import CampaignSingleSelectDropdown from 'components/DropdownSelect/SingleSelectDropdown/Usage/CampaignSingleSelectDropdown';
import StoresMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/StoresMultiSelectDropdown';
import { useActivityWizardContext } from 'pages/Activities/ActivityWizard/activityWizard.context';
import { useTheme } from '@mui/material/styles';

const CreateActivityDetails = (): JSX.Element => {
  const { newActivity, setNewActivity } = useActivityWizardContext();
  const theme = useTheme();

  const handleUpdateTags = (updateTags: DropdownOption[]) => {
    setNewActivity({
      ...newActivity,
      tagIds: updateTags.map((tag) => Number(tag.id)),
    });
  };
  const handleUpdateActivityAssignedUsers = (updatedUsers: DropdownOption[]) => {
    setNewActivity({
      ...newActivity,
      assignedUserIds: updatedUsers.map((user) => Number(user.id)),
    });
  };
  const handleUpdateStores = (updatedStores: DropdownOption[]) => {
    setNewActivity({
      ...newActivity,
      assignedStoreIds: updatedStores.map((store) => Number(store.id)),
    });
  };

  const handleUpdateActivityCcRecipients = (updatedUsers: DropdownOption[]) => {
    setNewActivity({
      ...newActivity,
      ccRecipientIds: updatedUsers.map((user) => Number(user.id)),
    });
  };

  const handleChangeField = (value: string | number | undefined, key: keyof Activity) =>
    setNewActivity({ ...newActivity, [key]: value });

  const fields: FormFieldInterface[] = [
    {
      label: 'Subject',
      value: newActivity.subject,
      onChange: (subject) => handleChangeField(subject, 'subject'),
      fullWidth: true,
      requiredMinimumLength: 1,
    },
    {
      label: 'Description',
      value: newActivity.description,
      onChange: (description) => handleChangeField(description, 'description'),
      fullWidth: true,
      multiLine: true,
      richTextEditor: true,
      // requiredMinimumLength: 1, // TODO - Removed until Activity Wizard form validation ticket
    },
    {
      label: 'Start Date',
      type: 'date',
      md: 6,
      // @ts-ignore
      value: newActivity.startDate,
      onChange: (startDate) => handleChangeField(startDate, 'startDate'),
    },
    {
      label: 'Due Date',
      type: 'date',
      md: 6,
      // @ts-ignore
      value: newActivity.dueDate,
      onChange: (dueDate) => handleChangeField(dueDate, 'dueDate'),
    },
    {
      label: 'Tags',
      onChange: () => {},
      customField: (
        <TagMultiSelectDropdown selectedTagIds={newActivity.tagIds} onChange={handleUpdateTags} />
      ),
      fullWidth: true,
    },
    {
      label: 'Campaign',
      onChange: () => {},
      customField: (
        <CampaignSingleSelectDropdown
          label='Campaign'
          selectedCampaignId={newActivity.campaignId}
          onChange={(selectedOption?: DropdownOption) =>
            handleChangeField(selectedOption?.id, 'campaignId')
          }
        />
      ),
      fullWidth: true,
    },
    {
      label: 'Stores',
      onChange: () => {},
      customField: (
        <StoresMultiSelectDropdown
          selectedStoreIds={newActivity.assignedStoreIds}
          onChange={(selectedOptions: DropdownOption[]) => handleUpdateStores(selectedOptions)}
        />
      ),
      fullWidth: true,
    },
    {
      label: 'Assigned To',
      onChange: () => {},
      customField: (
        <UserSelectDropdown
          label='Assigned To'
          selectedUserIds={newActivity.assignedUserIds}
          onChange={(selectedOptions: DropdownOption[]) =>
            handleUpdateActivityAssignedUsers(selectedOptions)
          }
        />
      ),
      fullWidth: true,
    },
    {
      label: 'Cc Recipients',
      onChange: () => {},
      customField: (
        <UserSelectDropdown
          label='Cc Recipients'
          selectedUserIds={newActivity.ccRecipientIds}
          onChange={(selectedOptions: DropdownOption[]) =>
            handleUpdateActivityCcRecipients(selectedOptions)
          }
        />
      ),
      fullWidth: true,
    },
  ];

  return (
    <Grid
      container
      mt={5}
      sx={{
        backgroundColor: theme.colors.white,
        borderRadius: '0.375rem',
        padding: '2rem',
        maxWidth: '75rem',
        mx: 'auto',
        marginBottom: '1.5rem',
      }}
    >
      <Grid item xs={0} md={2} />
      <Grid item xs={12} md={8}>
        <Form fields={fields} disclaimer='* denotes required field' />
      </Grid>
    </Grid>
  );
};

export default CreateActivityDetails;
